/**
 * Collaborator Notification Utilities
 * Performance-optimized helpers for collaborator notifications
 * 
 * @description Provides utilities for:
 * - Type checking
 * - Icon rendering
 * - Message formatting
 * - Action URL generation
 * - Toast content generation
 */

import React from 'react';
import {
  FaUserPlus,
  FaHandshake,
  FaCheckCircle,
  FaTimesCircle,
  FaStar,
  FaAward,
  FaThumbsUp,
  FaThumbsDown,
} from 'react-icons/fa';

import {
  COLLABORATOR_NOTIFICATION_TYPES,
  COLLABORATOR_NOTIFICATION_TYPES_SET,
  COLLABORATOR_NOTIFICATION_COLORS,
  COLLABORATOR_NOTIFICATION_PRIORITIES,
  COLLABORATOR_PRIORITY_STYLES,
  COLLABORATOR_ACTION_REQUIRED,
  ENDORSEMENT_LEVEL_BADGES,
} from '../../../constants/collaboratorNotificationConstants';

// ============================================================================
// TYPE CHECKING
// ============================================================================

/**
 * Check if a notification type is a collaborator notification
 * Uses Set for O(1) lookup performance
 * @param {string} type - Notification type
 * @returns {boolean}
 */
export const isCollaboratorNotificationType = (type) => {
  return COLLABORATOR_NOTIFICATION_TYPES_SET.has(type);
};

/**
 * Check if notification requires user action
 * @param {string} type - Notification type
 * @returns {boolean}
 */
export const isActionRequired = (type) => {
  return COLLABORATOR_ACTION_REQUIRED[type] || false;
};

// ============================================================================
// ICON MAPPING - Static elements for each notification type
// ============================================================================

const COLLABORATOR_ICON_MAP = Object.freeze({
  [COLLABORATOR_NOTIFICATION_TYPES.NEW_FOLLOWER]: (
    <FaUserPlus className="notification-icon collaborator-follower" />
  ),
  [COLLABORATOR_NOTIFICATION_TYPES.COLLABORATION_REQUEST_RECEIVED]: (
    <FaHandshake className="notification-icon collaborator-request-received" />
  ),
  [COLLABORATOR_NOTIFICATION_TYPES.COLLABORATION_REQUEST_ACCEPTED]: (
    <FaCheckCircle className="notification-icon collaborator-request-accepted" />
  ),
  [COLLABORATOR_NOTIFICATION_TYPES.COLLABORATION_REQUEST_REJECTED]: (
    <FaTimesCircle className="notification-icon collaborator-request-rejected" />
  ),
  [COLLABORATOR_NOTIFICATION_TYPES.RATING_RECEIVED]: (
    <FaStar className="notification-icon collaborator-rating" />
  ),
  [COLLABORATOR_NOTIFICATION_TYPES.RATING_RESPONSE_RECEIVED]: (
    <FaStar className="notification-icon collaborator-rating" />
  ),
  [COLLABORATOR_NOTIFICATION_TYPES.ENDORSEMENT_RECEIVED]: (
    <FaAward className="notification-icon collaborator-endorsement" />
  ),
  [COLLABORATOR_NOTIFICATION_TYPES.ENDORSEMENT_ACCEPTED]: (
    <FaThumbsUp className="notification-icon collaborator-endorsement-accepted" />
  ),
  [COLLABORATOR_NOTIFICATION_TYPES.ENDORSEMENT_DECLINED]: (
    <FaThumbsDown className="notification-icon collaborator-endorsement-declined" />
  ),
});

/**
 * Get React icon element for a collaborator notification type
 * @param {string} type - Notification type
 * @returns {React.ReactElement|null}
 */
export const getCollaboratorNotificationIcon = (type) => {
  return COLLABORATOR_ICON_MAP[type] || null;
};

// ============================================================================
// MESSAGE FORMATTING
// ============================================================================

/**
 * Generate toast message for collaborator notification
 * @param {Object} notification - Notification object
 * @returns {string}
 */
export const getCollaboratorNotificationMessage = (notification) => {
  const { type, data = {}, message } = notification;

  switch (type) {
    case COLLABORATOR_NOTIFICATION_TYPES.NEW_FOLLOWER:
      return `👤 ${data.followerName || 'Someone'} started following you`;

    case COLLABORATOR_NOTIFICATION_TYPES.COLLABORATION_REQUEST_RECEIVED:
      return `🤝 ${data.requesterName || 'Someone'} invited you to collaborate on "${data.researchRoomName || 'a research room'}"`;

    case COLLABORATOR_NOTIFICATION_TYPES.COLLABORATION_REQUEST_ACCEPTED:
      return `✅ ${data.recipientName || 'Your request'} accepted your collaboration request! 🎉`;

    case COLLABORATOR_NOTIFICATION_TYPES.COLLABORATION_REQUEST_REJECTED:
      return `Your collaboration request was declined`;

    case COLLABORATOR_NOTIFICATION_TYPES.RATING_RECEIVED:
      if (data.isAnonymous) {
        return `⭐ Someone rated your collaboration`;
      }
      return `⭐ ${data.raterName || 'Someone'} rated your collaboration`;

    case COLLABORATOR_NOTIFICATION_TYPES.RATING_RESPONSE_RECEIVED:
      return `⭐ You received a response to your rating`;

    case COLLABORATOR_NOTIFICATION_TYPES.ENDORSEMENT_RECEIVED: {
      const levelBadge = ENDORSEMENT_LEVEL_BADGES[data.endorsementLevel] || '';
      return `🏆 ${data.endorserName || 'Someone'} endorsed your ${data.skill || 'skills'} ${levelBadge}`;
    }

    case COLLABORATOR_NOTIFICATION_TYPES.ENDORSEMENT_ACCEPTED:
      return `👍 ${data.endorseeName || 'Your endorsement'} accepted your endorsement for ${data.skill || 'a skill'} ✅`;

    case COLLABORATOR_NOTIFICATION_TYPES.ENDORSEMENT_DECLINED:
      return `Your endorsement for ${data.skill || 'a skill'} was declined`;

    default:
      return message || 'New notification';
  }
};

// ============================================================================
// ACTION URL GENERATION
// ============================================================================

/**
 * Get action URL for collaborator notification
 * @param {Object} notification - Notification object
 * @returns {string|null}
 */
export const getCollaboratorActionUrl = (notification) => {
  const { type, data = {}, actionUrl, sender } = notification;

  // Use actionUrl from backend if available
  if (actionUrl) return actionUrl;

  // Generate URL based on type
  switch (type) {
    case COLLABORATOR_NOTIFICATION_TYPES.NEW_FOLLOWER:
      return `/profile/${sender?._id || sender || data.followerId}`;

    case COLLABORATOR_NOTIFICATION_TYPES.COLLABORATION_REQUEST_RECEIVED:
      return '/network/requests';

    case COLLABORATOR_NOTIFICATION_TYPES.COLLABORATION_REQUEST_ACCEPTED:
      return `/research-room/${data.researchRoomId}`;

    case COLLABORATOR_NOTIFICATION_TYPES.COLLABORATION_REQUEST_REJECTED:
      return '/network/requests';

    case COLLABORATOR_NOTIFICATION_TYPES.RATING_RECEIVED:
      return '/profile?tab=ratings';

    case COLLABORATOR_NOTIFICATION_TYPES.RATING_RESPONSE_RECEIVED:
      return '/profile?tab=ratings';

    case COLLABORATOR_NOTIFICATION_TYPES.ENDORSEMENT_RECEIVED:
      return '/profile?tab=skills';

    case COLLABORATOR_NOTIFICATION_TYPES.ENDORSEMENT_ACCEPTED:
      return `/profile/${data.endorseeId || sender?._id}?tab=skills`;

    case COLLABORATOR_NOTIFICATION_TYPES.ENDORSEMENT_DECLINED:
      return null;

    default:
      return null;
  }
};

// ============================================================================
// PRIORITY & STYLING
// ============================================================================

/**
 * Get priority for collaborator notification
 * Ratings with 4+ stars are high priority
 * @param {Object} notification - Notification object
 * @returns {string}
 */
export const getCollaboratorNotificationPriority = (notification) => {
  const { type, data = {}, priority } = notification;

  // Use backend-provided priority if available
  if (priority) return priority;

  // Special case: high rating
  if (type === COLLABORATOR_NOTIFICATION_TYPES.RATING_RECEIVED) {
    if (data.overallRating >= 4) {
      return 'high';
    }
  }

  return COLLABORATOR_NOTIFICATION_PRIORITIES[type] || 'normal';
};

/**
 * Get style configuration for collaborator notification
 * @param {Object} notification - Notification object
 * @returns {Object}
 */
export const getCollaboratorNotificationStyle = (notification) => {
  const priority = getCollaboratorNotificationPriority(notification);
  return COLLABORATOR_PRIORITY_STYLES[priority] || COLLABORATOR_PRIORITY_STYLES.normal;
};

/**
 * Get color class for collaborator notification type
 * @param {string} type - Notification type
 * @returns {string}
 */
export const getCollaboratorNotificationColor = (type) => {
  return COLLABORATOR_NOTIFICATION_COLORS[type] || 'blue';
};

// ============================================================================
// TOAST CONTENT GENERATION
// ============================================================================

/**
 * Generate toast title based on notification type
 * @param {string} type - Notification type
 * @returns {string}
 */
export const getCollaboratorToastTitle = (type) => {
  switch (type) {
    case COLLABORATOR_NOTIFICATION_TYPES.NEW_FOLLOWER:
      return '👤 New Follower';
    case COLLABORATOR_NOTIFICATION_TYPES.COLLABORATION_REQUEST_RECEIVED:
      return '🤝 Collaboration Request';
    case COLLABORATOR_NOTIFICATION_TYPES.COLLABORATION_REQUEST_ACCEPTED:
      return '🎉 Request Accepted';
    case COLLABORATOR_NOTIFICATION_TYPES.COLLABORATION_REQUEST_REJECTED:
      return 'Request Declined';
    case COLLABORATOR_NOTIFICATION_TYPES.RATING_RECEIVED:
      return '⭐ New Rating';
    case COLLABORATOR_NOTIFICATION_TYPES.RATING_RESPONSE_RECEIVED:
      return '⭐ Rating Response';
    case COLLABORATOR_NOTIFICATION_TYPES.ENDORSEMENT_RECEIVED:
      return '🏆 Skill Endorsed';
    case COLLABORATOR_NOTIFICATION_TYPES.ENDORSEMENT_ACCEPTED:
      return '✅ Endorsement Accepted';
    case COLLABORATOR_NOTIFICATION_TYPES.ENDORSEMENT_DECLINED:
      return 'Endorsement Declined';
    default:
      return 'Network';
  }
};

// ============================================================================
// RATING HELPERS
// ============================================================================

/**
 * Get star display for rating value
 * @param {number} rating - Rating value (1-5)
 * @returns {string}
 */
export const getRatingStars = (rating) => {
  const fullStars = Math.floor(rating);
  const halfStar = rating % 1 >= 0.5;
  let stars = '★'.repeat(fullStars);
  if (halfStar) stars += '½';
  stars += '☆'.repeat(5 - fullStars - (halfStar ? 1 : 0));
  return stars;
};

/**
 * Get recommendation level display
 * @param {string} level - Recommendation level
 * @returns {string}
 */
export const getRecommendationDisplay = (level) => {
  const displays = {
    highly_recommend: '💚 Highly Recommended',
    recommend: '👍 Recommended',
    neutral: '😐 Neutral',
    not_recommend: '👎 Not Recommended',
  };
  return displays[level] || level;
};

// ============================================================================
// ENDORSEMENT HELPERS
// ============================================================================

/**
 * Get endorsement level badge
 * @param {string} level - Endorsement level
 * @returns {string}
 */
export const getEndorsementLevelBadge = (level) => {
  return ENDORSEMENT_LEVEL_BADGES[level] || '';
};

/**
 * Get endorsement level display
 * @param {string} level - Endorsement level
 * @returns {string}
 */
export const getEndorsementLevelDisplay = (level) => {
  const badge = ENDORSEMENT_LEVEL_BADGES[level] || '';
  const levelText = level ? level.charAt(0).toUpperCase() + level.slice(1) : '';
  return `${badge} ${levelText}`.trim();
};

export default {
  isCollaboratorNotificationType,
  isActionRequired,
  getCollaboratorNotificationIcon,
  getCollaboratorNotificationMessage,
  getCollaboratorActionUrl,
  getCollaboratorNotificationPriority,
  getCollaboratorNotificationStyle,
  getCollaboratorNotificationColor,
  getCollaboratorToastTitle,
  getRatingStars,
  getRecommendationDisplay,
  getEndorsementLevelBadge,
  getEndorsementLevelDisplay,
};
